import type {FormEventHandler} from 'react';
import React, {useCallback, useEffect, useState} from 'react';
import {getCookie} from '../../../utils/cookies';
import {authService} from '../../../services/auth.service';
import {env} from "../../../config/environment";
import {brand} from "../../../theme/brand";
import {useNavigate, useSearchParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {useSnackbar} from "../../../contexts/SnackbarContext";
import {usePageTitle} from '../../../hooks/usePageTitle';
import {PageTitleKeys, RoutePaths} from '../../../config/routeConstants';
import {parseError} from "../../../errors/http.service";
import {Button, CircularProgress, IconButton, InputAdornment, Typography} from '@mui/material';
import {Visibility, VisibilityOff} from '@mui/icons-material';
import {accessTokenKey, authRedirectReason} from '../../../core/AuthInterceptor';
import {ErrorText, ForgotPasswordWrapper, FormWrapper, LoginCard, RegisterLink, StyledTextField} from './Login.styled';
import {AuthContainer} from "../Auth.styled";
import LogoWithLanguageSwitcher from '../../../components/common/LogoWithLanguageSwitcher';
import {validatePassword} from "../../../utils/password-validation";
import {getEmailValidationError} from "../../../utils/email-validation";
import ResetPasswordRedirectDialog from "./components/ResetPasswordRedirectDialog";
import {EXTERNAL_LOGIN_ERROR_MESSAGE} from "../../../models/admin-config.models";
import {termsService} from "../../../services/terms.service";

const Login = () => {
    usePageTitle(PageTitleKeys.Login);

    const [searchParams, setSearchParams] = useSearchParams();
    const errorCode = searchParams.get('error');

    const {t} = useTranslation();
    const navigate = useNavigate();
    const {showSnackbar} = useSnackbar();

    const isRegistrationEnabled = env.registrationEnabled;
    const isEmailRequired = env.emailLoginRequired;
    const hasExternalPasswordReset = env.hasExternalPasswordReset;

    const [username, setUsername] = useState('');
    const [emailTouched, setEmailTouched] = useState(false);

    const [password, setPassword] = useState('');
    const [passwordTouched, setPasswordTouched] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const [submitted, setSubmitted] = useState(false);
    const [isFetchingToken, setIsFetchingToken] = useState(false);
    const [loginError, setLoginError] = useState(false);

    const [openResetPasswordDialog, setOpenResetPasswordDialog] = useState(false);

    const emailValidationError = getEmailValidationError(username, isEmailRequired);
    const passwordValidationError = validatePassword(password);
    const showEmailError = (emailTouched || submitted) && !!emailValidationError;
    const showPasswordError = (passwordTouched || submitted) && !!passwordValidationError;

    useEffect(() => {
        if (errorCode) {
            showSnackbar(t(EXTERNAL_LOGIN_ERROR_MESSAGE[errorCode]), 'error');
            setSearchParams({}, { replace: true });
        }
    }, [errorCode, t]);

    useEffect(() => {
        authService.clearPendingTwoFactorEmail();
        termsService.clearPendingEulaVersion();

        const redirectReason = localStorage.getItem(authRedirectReason);
        if (redirectReason === 'unauthorized') {
            showSnackbar(t('login.relogin'), 'warning');
            localStorage.removeItem(authRedirectReason);
        }

        const hasToken = !!getCookie(accessTokenKey);
        if (hasToken) {
            navigate(`/${RoutePaths.Chat}`, {replace: true});
        }
    }, [navigate, showSnackbar, t]);

    const handleLogin = useCallback<FormEventHandler<HTMLFormElement>>(async (event) => {
        event.preventDefault();
        setSubmitted(true);

        if (passwordValidationError || emailValidationError) return;

        setIsFetchingToken(true);
        setLoginError(false);
        termsService.clearPendingEulaVersion();
        authService.clearPendingTwoFactorEmail();

        try {
            const response = await authService.loginRequest(username, password);

            if (response.twoFactorAuth) {
                authService.setPendingTwoFactorEmail(response.username ?? username);
                navigate(`/${RoutePaths.VerifyTwoFactor}`, {replace: true});
                return;
            }

            authService.finalizeAuthentication(response, username);

            if (response.eulaAcceptanceRequired) {
                termsService.setPendingEulaVersion(response.eulaVersion);
                navigate(`/eula`, {replace: true});
            }

            navigate(`/dashboard`, {replace: true});
        } catch (error: unknown) {
            console.error(error);
            if (
                typeof error === 'object'
                && error !== null
                && 'response' in error
                && [400, 401].includes(
                    ((error as { response?: { status?: number } }).response?.status ?? 0),
                )
            ) {
                setLoginError(true);
            } else {
                showSnackbar(parseError(error), "error");
            }
        } finally {
            setIsFetchingToken(false);
        }
    }, [navigate, password, passwordValidationError, showSnackbar, username]);

    const handleFieldChange = () => {
        if (loginError) {
            setLoginError(false);
        }
    };

    const handleRegistration = (e: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>) => {
        if (e.type === 'keydown') {
            const keyEvent = e as React.KeyboardEvent<HTMLDivElement>;
            if (keyEvent.key !== 'Enter' && keyEvent.key !== ' ') {
                return;
            }
            e.preventDefault();
        }

        if (!isRegistrationEnabled) return;
        navigate(`/${RoutePaths.Register}`);
    };

    const handleResetPassword= (e: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>) => {
        if (e.type === 'keydown') {
            const keyEvent = e as React.KeyboardEvent<HTMLDivElement>;
            if (keyEvent.key !== 'Enter' && keyEvent.key !== ' ') {
                return;
            }
            e.preventDefault();
        }

        if (!hasExternalPasswordReset) {
            navigateToResetPassword();
            return;
        }

        setOpenResetPasswordDialog(true);
    };

    const handleEntraIdLogin= (e: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>) => {
        if (e.type === 'keydown') {
            const keyEvent = e as React.KeyboardEvent<HTMLDivElement>;
            if (keyEvent.key !== 'Enter' && keyEvent.key !== ' ') {
                return;
            }
            e.preventDefault();
        }

        authService.fetchEntraLogin();
    };

    const navigateToResetPassword = () => {
        navigate(`/${RoutePaths.RequestPasswordReset}`);
    };

    const handleRedirectToExternalPasswordReset = () => {
        if (!hasExternalPasswordReset) return;
        const redirectUrl = env.passwordResetUrl;
        window.open(redirectUrl, '_blank', 'noopener,noreferrer');
    };

    return (
        <AuthContainer role="region" aria-labelledby="login-title">
            <LogoWithLanguageSwitcher src={brand.logo} alt={t('auth.logo.alt')} clickable={true}/>

            <LoginCard>
                <Typography id="login-title" variant="h5" align="center" width="100%" gutterBottom>
                    {t('login.title')}
                </Typography>
                <Typography variant="body2" align="center" color="textSecondary" marginBottom="16px">
                    {t('login.welcome')}
                </Typography>

                <FormWrapper onSubmit={handleLogin} aria-live="polite" aria-atomic={true} aria-busy={isFetchingToken}>
                    <div>
                        <StyledTextField
                            label={t('login.username')}
                            fullWidth
                            margin="normal"
                            value={username}
                            onChange={(e) => {
                                setUsername(e.target.value);
                                handleFieldChange();
                            }}
                            onBlur={() => setEmailTouched(true)}
                            autoComplete="on"
                            disabled={isFetchingToken}
                            error={showEmailError || loginError}
                            helperText={showEmailError ? String(t(emailValidationError!.key)) : ""}
                            inputProps={{ 'aria-label': t('login.usernameAriaLabel') as string }}
                        />
                    </div>

                    <div>
                        <StyledTextField
                            label={t('login.password')}
                            type={showPassword ? 'text' : 'password'}
                            fullWidth
                            margin="normal"
                            value={password}
                            onChange={(e) => {
                                setPassword(e.target.value);
                                handleFieldChange();
                            }}
                            onBlur={() => setPasswordTouched(true)}
                            autoComplete="on"
                            disabled={isFetchingToken}
                            error={showPasswordError || loginError}
                            helperText={showPasswordError ? String(t(passwordValidationError!.key)) : ""}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword((prev) => !prev)}
                                            edge="end"
                                            sx={{color: 'primary.main'}}
                                            disabled={isFetchingToken}
                                            aria-label={t('login.toggleVisibility') as string}
                                        >
                                            {showPassword ? <VisibilityOff/> : <Visibility/>}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            inputProps={{ 'aria-label': t('login.passwordAriaLabel') as string }}
                        />

                        <div style={{height: '24px'}}>
                            {loginError && <ErrorText>{t('login.invalidCredentials')}</ErrorText>}
                        </div>
                    </div>

                    {isFetchingToken && (
                        <div role="status" aria-live="polite" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(1px, 1px, 1px, 1px)' }}>
                            {t('login.loading')}
                        </div>
                    )}

                    <ForgotPasswordWrapper
                        role="button"
                        tabIndex={0}
                        onClick={!isFetchingToken ? handleResetPassword : undefined}
                    >
                        {t('login.forgotPassword')}
                    </ForgotPasswordWrapper>

                    <Button
                        fullWidth
                        type="submit"
                        variant="contained"
                        disabled={isFetchingToken || !username || !password || loginError || showPasswordError}
                    >
                        {isFetchingToken
                            ? <CircularProgress size={24} sx={{color: 'inherit'}}/>
                            : t('login.loginButton')
                        }
                    </Button>

                    {isRegistrationEnabled && (
                        <RegisterLink
                            role="button"
                            tabIndex={0}
                            onClick={isFetchingToken ? undefined : handleRegistration}
                            disabled={isFetchingToken}
                        >
                            {t('login.register')}
                        </RegisterLink>
                    )}

                    <RegisterLink
                        role="button"
                        tabIndex={0}
                        onClick={!isFetchingToken ? handleEntraIdLogin : undefined}
                        disabled={isFetchingToken}
                    >
                        {t('login.loginViaEntraId')}
                    </RegisterLink>
                </FormWrapper>
            </LoginCard>

            {openResetPasswordDialog && (
                <ResetPasswordRedirectDialog
                    open={openResetPasswordDialog}
                    onAppResetClick={navigateToResetPassword}
                    onRedirectClick={handleRedirectToExternalPasswordReset}
                    onClose={() => setOpenResetPasswordDialog(false)}
                />
            )}
        </AuthContainer>
    );
};

export default Login;
